'use client';

import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { saveIndicesData } from '../../services/TradingMallService';
import { getSessionData } from '@repo/shared-utils/common-utils';
import type { IndicesFilterModalProps } from '../../interfaces/MarketToday.interface';
import BFLModal from '@repo/shared-ui/BFLModal';
import './IndicesFilterModal.css';

const IndicesFilterModal = forwardRef<{ open: () => void }, IndicesFilterModalProps>(({ modalData, stockData, content, onDataRefresh }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<string[]>([]);

  useImperativeHandle(ref, () => ({
    open: () => {
      if (stockData?.indicesList) {
        const selected = stockData?.indicesList?.map((item) => item.symbol);
        setSelectedIndices(selected);
      }
      setIsOpen(true);
    }
  }));

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleCheckboxChange = (symbol: string) => {
    setSelectedIndices(prev => 
      prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
    );
  };

  const handleRemoveTag = (symbol: string) => {
    setSelectedIndices(prev => prev.filter(s => s !== symbol));
  };

  const handleClearData = () => {
    setSelectedIndices([]);
  };

  const handleApply = async () => {
    const minimumIndices = Number(content?.minimumIndices);
    const maximumIndices = Number(content?.maximumIndices);
    if (selectedIndices.length < minimumIndices || selectedIndices.length > maximumIndices) {
      return;
    }
    try {
      const { mobileNo } = await getSessionData();
      await saveIndicesData({ mobileNo, indices: selectedIndices });
      handleClose();
      // Refresh data after saving - this will call getIndicesDetails for logged-in users
      if (onDataRefresh) {
        await onDataRefresh();
      }
    } catch (error) {
      console.error('Error saving indices:', error);
    }
  };

  const showMinWarning = selectedIndices.length < Number(content?.minimumIndices);
  const showMaxWarning = selectedIndices.length >= Number(content?.maximumIndices);

  return (
    <BFLModal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      showHeader={false}
      modalClassName="md:items-center"
      maxWidthClassName="lg:max-w-[540px]"
      contentClassName="w-full max-h-[90vh] flex flex-col p-0"
      enableScroll={false}
    >
      <div className="flex flex-col h-full max-h-[90vh] -mx-4">
        <button onClick={handleClose} className="absolute top-4 right-4 text-orange-400 hover:text-orange-600 cursor-pointer bg-transparent border-0 p-0 z-10">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        <header className="border-b border-gray-200 px-4 py-3 bg-[#e5e5e5] md:bg-transparent">
          <div className="text-sm md:text-lg font-medium">{content?.filterTitle}</div>
        </header>

        <div className="flex items-center gap-3 p-4 border-b border-gray-200">
          <span className="text-xs md:text-sm whitespace-nowrap">{content?.selectedText} ({selectedIndices.length}/{content?.maximumIndices}):</span>
          <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {selectedIndices.map(symbol => (
              <button key={symbol} className="flex items-center gap-2 px-1.5 py-2 bg-gray-100 rounded border border-black-300 cursor-pointer whitespace-nowrap" onClick={() => handleRemoveTag(symbol)}>
                <span className="text-xs md:text-[14px] font-medium text-[#4a6784]">{symbol}</span>
                <svg className="w-5 h-5 text-[#4a6784]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          <aside className="min-w-[119px] border-t border-gray-200">
            <div className="relative border-l-2 border-orange-500 p-4 text-orange-500">
              <div className="text-xs md:text-sm font-medium">{content?.regionText} ({selectedIndices.length})</div>
            </div>
          </aside>

          <main className="flex-1 border border-gray-200 p-4 overflow-y-auto custom-shadow">
            <div className="space-y-2">
              {modalData.indicesList?.map((item) => (
                <div key={item.symbol} className="flex items-start gap-2 py-2">
                  <input
                    type="checkbox"
                    disabled={selectedIndices.length >= Number(content?.maximumIndices) && !selectedIndices.includes(item.symbol)}
                    checked={selectedIndices.includes(item.symbol)}
                    onChange={() => handleCheckboxChange(item.symbol)}
                    className="mt-1 custom-checkbox"
                  />
                  <div>
                    <span className="block text-sm">{item.symbol}</span>
                    <div className="flex gap-4 text-xs">
                      <div className="flex items-center gap-1">
                        <span className={item.stockStatus === 'positive' ? 'text-green-700' : 'text-red-700'}>{item.ltp}</span>
                        <span className={item.stockStatus === 'positive' ? 'text-green-600' : 'text-red-600'}>{item.stockStatus === 'positive' ? '▲' : '▼'}</span>
                      </div>
                      <span className="text-gray-600">{item.changePrice} ({item.perChange}%)</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </main>
        </div>

        <footer className="p-4 border-t border-gray-200">
          {showMinWarning && <p className="text-xs text-red-600 text-center mb-3">{content?.alertForMinSelection}</p>}
          {showMaxWarning && <p className="text-xs text-red-600 text-center mb-3">{content?.alertForMaxSelection}</p>}
          <div className="flex justify-between items-center">
            <button onClick={handleClearData} className="text-sm font-medium text-orange-500 border-0 bg-transparent cursor-pointer">{content?.clearFilterText}</button>
            <button onClick={handleApply} className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-full min-w-[200px] cursor-pointer">{content?.applyText}</button>
          </div>
        </footer>
      </div>
    </BFLModal>
  );
});

IndicesFilterModal.displayName = 'IndicesFilterModal';

export default IndicesFilterModal;
